import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl";
import { makeStyles } from "@material-ui/core/styles";

import "mapbox-gl/dist/mapbox-gl.css";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: 0,
  },
  map: {
    position: "absolute",
    inset: 0,
  },
  markerLayer: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    pointerEvents: "none",
  },
  marker: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    padding: 0,
    pointerEvents: "auto",
    willChange: "transform, left, top",
    transform: "translate(-50%, -100%)",
  },
  bubble: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontSize: 18,
    fontWeight: 700,
    border: "3px solid rgba(255,255,255,0.95)",
    boxShadow: "0 8px 18px rgba(0,0,0,0.28)",
    transform: "translateY(1px)",
  },
  bubbleActive: {
    transform: "translateY(1px) scale(1.08)",
  },
  label: {
    marginTop: 4,
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.84)",
    color: "#fff",
    fontSize: 11,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
  },
  stem: {
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderTop: "10px solid rgba(255,255,255,0.95)",
    marginTop: -1,
  },
  active: {
    transform: "translate(-50%, -100%)",
  },
  empty: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    color: "#cbd5e1",
    background: "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(30,41,59,0.88))",
    textAlign: "center",
    padding: 16,
  },
  emptyInner: {
    maxWidth: 320,
    fontSize: 13,
    lineHeight: 1.7,
  },
}));

const TRUST_COLOR = {
  4: "#22c55e",
  3: "#eab308",
  2: "#fb923c",
  1: "#ef4444",
};

const DEFAULT_CENTER = [139.767125, 35.681236];
const DEFAULT_ZOOM = 11.7;
const MAP_STYLE_URL = "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb";

const getBounds = (plots) => {
  if (!plots || plots.length === 0) {
    return null;
  }

  return plots.reduce(
    (bounds, plot) => bounds.extend([plot.lng, plot.lat]),
    new mapboxgl.LngLatBounds([plots[0].lng, plots[0].lat], [plots[0].lng, plots[0].lat])
  );
};

const getMarkerLabel = (plot) => {
  const assetShort =
    plot.assetType === "井戸" ? "井" :
    plot.assetType === "電柱" ? "柱" :
    plot.assetType === "消火栓" ? "消" :
    plot.assetType === "マンホール" ? "蓋" :
    plot.assetType === "防火水槽" ? "防" :
    plot.assetType?.slice(0, 1) || "●";

  return assetShort;
};

const PlotMapboxMap = ({
  plots,
  activePlotId,
  onSelectPlot,
  panelOpen,
  initialCenter,
  initialZoom,
}) => {
  const classes = useStyles();
  const mapContainerRef = useRef(null);
  const markerLayerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());
  const rafRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  const center = useMemo(() => initialCenter || DEFAULT_CENTER, [initialCenter]);
  const zoom = initialZoom || DEFAULT_ZOOM;

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !token) {
      return;
    }

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center,
      zoom,
      localIdeographFontFamily: "'Noto Sans JP', 'Hiragino Sans', 'Yu Gothic', sans-serif",
      attributionControl: true,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.on("load", () => {
      setMapReady(true);
      map.resize();
      map.triggerRepaint();
    });
    map.on("error", (event) => {
      // eslint-disable-next-line no-console
      console.error("Mapbox map error", event?.error || event);
    });

    mapRef.current = map;
    const markers = markersRef.current;

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      markers.clear();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [center, zoom, token]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;
    const markerLayer = markerLayerRef.current;
    if (!markerLayer) {
      return;
    }

    markerLayer.innerHTML = "";
    markersRef.current.clear();

    const renderMarkers = () => {
      if (!mapRef.current || !markerLayerRef.current) {
        return;
      }

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = window.requestAnimationFrame(() => {
        if (!mapRef.current || !markerLayerRef.current) {
          return;
        }

        markerLayerRef.current.querySelectorAll("[data-plot-id]").forEach((node) => {
          const plotId = node.getAttribute("data-plot-id");
          const plot = markersRef.current.get(plotId);
          if (!plot) {
            return;
          }
          const point = map.project([plot.lng, plot.lat]);
          node.style.left = `${point.x}px`;
          node.style.top = `${point.y}px`;
        });
      });
    };

    const addMarkers = () => {
      if (!markerLayerRef.current) {
        return;
      }

      markerLayerRef.current.innerHTML = "";
      markersRef.current.clear();

      if (!plots || plots.length === 0) {
        return;
      }

      const bounds = getBounds(plots);
      if (bounds) {
        if (plots.length === 1) {
          map.easeTo({
            center: [plots[0].lng, plots[0].lat],
            zoom: Math.max(zoom, 14),
            duration: 400,
          });
        } else {
          map.fitBounds(bounds, {
            padding: { top: 80, bottom: 80, left: 80, right: 80 },
            duration: 450,
          });
        }
      }

      plots.forEach((plot) => {
        markersRef.current.set(plot.id, plot);

        const el = document.createElement("button");
        el.type = "button";
        el.className = classes.marker;
        el.dataset.plotId = plot.id;
        el.setAttribute("aria-label", `${plot.municipality} ${plot.assetType}`);
        el.innerHTML = `
          <span class="${classes.bubble} ${plot.id === activePlotId ? classes.bubbleActive : ""}" style="background:${TRUST_COLOR[plot.lv] || "#64748b"}">${getMarkerLabel(plot)}</span>
          <span class="${classes.label}">${plot.municipality}</span>
          <span class="${classes.stem}"></span>
        `;
        el.addEventListener("click", () => onSelectPlot(plot));

        markerLayerRef.current.appendChild(el);
      });

      renderMarkers();
    };

    addMarkers();

    const handleMove = () => renderMarkers();
    map.on("move", handleMove);
    map.on("zoom", handleMove);
    map.on("rotate", handleMove);
    map.on("pitch", handleMove);
    map.on("resize", handleMove);
    map.on("moveend", handleMove);

    return () => {
      map.off("move", handleMove);
      map.off("zoom", handleMove);
      map.off("rotate", handleMove);
      map.off("pitch", handleMove);
      map.off("resize", handleMove);
      map.off("moveend", handleMove);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [
    activePlotId,
    classes.bubble,
    classes.bubbleActive,
    classes.label,
    classes.marker,
    classes.stem,
    mapReady,
    onSelectPlot,
    plots,
    token,
    zoom,
  ]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      mapRef.current && mapRef.current.resize();
    }, 220);

    return () => window.clearTimeout(timer);
  }, [mapReady, panelOpen, plots]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    markerLayerRef.current?.querySelectorAll("[data-plot-id]").forEach((node) => {
      const plotId = node.getAttribute("data-plot-id");
      const plot = markersRef.current.get(plotId);
      if (!plot) {
        return;
      }
      const bubble = node.querySelector(`.${classes.bubble}`);
      if (bubble) {
        bubble.className = `${classes.bubble}${plot.id === activePlotId ? ` ${classes.bubbleActive}` : ""}`;
        bubble.style.background = TRUST_COLOR[plot.lv] || "#64748b";
        bubble.textContent = getMarkerLabel(plot);
      }
      node.setAttribute("aria-label", `${plot.municipality} ${plot.assetType}`);
    });
  }, [activePlotId, classes.bubble, classes.bubbleActive, mapReady, plots]);

  if (!token) {
    return (
      <div className={classes.root}>
        <div className={classes.empty}>
          <div className={classes.emptyInner}>
            `REACT_APP_MAPBOX_ACCESS_TOKEN` が未設定です。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div ref={mapContainerRef} className={classes.map} />
      <div ref={markerLayerRef} className={classes.markerLayer} />
      {!mapReady && (
        <div className={classes.empty}>
          <div className={classes.emptyInner}>地図を読み込み中です。</div>
        </div>
      )}
    </div>
  );
};

PlotMapboxMap.propTypes = {
  plots: PropTypes.array.isRequired,
  activePlotId: PropTypes.string,
  onSelectPlot: PropTypes.func.isRequired,
  panelOpen: PropTypes.bool,
  initialCenter: PropTypes.array,
  initialZoom: PropTypes.number,
};

export default PlotMapboxMap;
